import React, { useEffect, useState } from "react";
import { Plus, Edit, Trash2, RefreshCw, X } from "lucide-react";
import { useGeneric } from "../../hooks/useGeneric";

const EMPTY_FORM = {
    partner_id: null,
    partner_name: "",
    mobile: "",
};

const Partners = () => {
    const [form, setForm] = useState(EMPTY_FORM);
    const [showForm, setShowForm] = useState(false);
    const [errors, setErrors] = useState({});
    const [search, setSearch] = useState("");
    const [saving, setSaving] = useState(false);

    const { data: partners = [], loading, fetchAll: fetchPartners, save: savePartner, remove: deletePartner } = useGeneric({
        tableName: "partners",
        idColumn: "partner_id",
        orderBy: "partner_name",
        removeOnInsert: ["partner_id"],
    });

    useEffect(() => {
        fetchPartners();
    }, [fetchPartners]);

    const set = (key, val) => {
        setForm((prev) => ({ ...prev, [key]: val }));
        setErrors((prev) => ({ ...prev, [key]: "" }));
    };

    // ── Validation ───────────────────────────────────────────────────
    const validate = () => {
        const errs = {};

        if (!form.partner_name.trim())
            errs.partner_name = "Partner name is required.";
        else if (form.partner_name.length > 50)
            errs.partner_name = "Max 50 characters.";

        if (!form.mobile.trim())
            errs.mobile = "Mobile number is required.";
        else if (!/^\d{10}$/.test(form.mobile.trim()))
            errs.mobile = "Mobile must be 10 digits.";

        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    // ── Save ─────────────────────────────────────────────────────────
    const onSave = async () => {
        if (!validate()) return;
        setSaving(true);
        try {
            await savePartner(form);
            closeModal();
        } catch (err) {
            console.error("Save failed", err);
            alert("Save failed: " + (err.message || "Unknown error"));
        } finally {
            setSaving(false);
        }
    };

    // ── Modal helpers ────────────────────────────────────────────────
    const openAdd = () => {
        setForm(EMPTY_FORM);
        setErrors({});
        setShowForm(true);
    };

    const openEdit = (partner) => {
        setForm(...partners)
        setErrors({});
        setShowForm(true);
    };

    const closeModal = () => {
        setShowForm(false);
        setForm(EMPTY_FORM);
        setErrors({});
    };

    // ── Delete ───────────────────────────────────────────────────────
    const handleDelete = (id) => {
        if (window.confirm("Are you sure you want to delete this partner?")) {
            deletePartner(id);
        }
    };

    // ── Filter ───────────────────────────────────────────────────────
    const filtered = (partners || [])
        .filter(
            (u) =>
                u.partner_name?.toLowerCase().includes(search.toLowerCase()) ||
                u.mobile?.includes(search)
        )
        .sort((a, b) => a.partner_name?.localeCompare(b.partner_name));

    return (
        <div className="cl-page">
            {/* ── Panel ── */}
            <div className="cl-panel">
                <div className="cl-panel-heading">
                    <span>Partner Details</span>
                    <button className="cl-add-btn" onClick={openAdd} title="Add Partner">
                        <Plus size={16} />
                    </button>
                </div>

                {/* Search bar */}
                <div className="cl-search-bar">
                    <input
                        type="text"
                        placeholder="Search partners…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="cl-search-input"
                    />
                    <button className="cl-refresh-btn" onClick={fetchPartners} title="Refresh">
                        <RefreshCw size={15} />
                    </button>
                </div>

                {/* Table */}
                {loading ? (
                    <div className="cl-loading">Loading…</div>
                ) : (
                    <div className="cl-table-wrapper">
                        <table className="cl-table">
                            <thead>
                                <tr>
                                    <th>Partner ID</th>
                                    <th>Partner Name</th>
                                    <th>Mobile Number</th>
                                    <th className="cl-actions-th">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.length === 0 ? (
                                    <tr>
                                        <td colSpan={5} className="cl-empty">
                                            No partners found.
                                        </td>
                                    </tr>
                                ) : (
                                    filtered.map((u, i) => (
                                        <tr
                                            key={u.partner_id ?? i}
                                            className={i % 2 === 0 ? "cl-row-even" : "cl-row-odd"}
                                        >
                                            <td data-label="ID">{u.partner_id}</td>
                                            <td data-label="Partner Name">{u.partner_name}</td>
                                            <td data-label="Mobile">{u.mobile}</td>
                                            <td className="cl-actions-td">
                                                <button
                                                    className="cl-icon-btn cl-edit"
                                                    onClick={() => openEdit(u)}
                                                    title="Edit"
                                                >
                                                    <Edit size={15} />
                                                </button>
                                                <button
                                                    className="cl-icon-btn cl-delete"
                                                    onClick={() => handleDelete(u.partner_id)}
                                                    title="Delete"
                                                >
                                                    <Trash2 size={15} />
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                )}

                <div className="cl-panel-footer" />
            </div>

            {/* ── Modal ── */}
            {showForm && (
                <div className="cl-modal-backdrop">
                    <div className="cl-modal-overlay" />
                    <div className="cl-modal" onClick={(e) => e.stopPropagation()}>

                        {/* Modal Header */}
                        <div className="cl-modal-header">
                            <h4 className="cl-modal-title">
                                {form.partner_id ? "Edit Partner" : "Add Partner"}
                            </h4>
                            <button className="cl-modal-close" onClick={closeModal}>
                                <X size={18} />
                            </button>
                        </div>

                        {/* Modal Body */}
                        <div className="cl-modal-body">
                            <form autoComplete="off">

                                {/* Row: Partner Name | Partner ID */}
                                <div className="cl-form-group cl-row">
                                    <div className="cl-col-10">
                                        <label className="cl-control-label">Partner Name</label>
                                        <input
                                            type="text"
                                            className={`cl-form-control${errors.partner_name ? " cl-input-error" : ""}`}
                                            placeholder="Partner Name"
                                            maxLength={50}
                                            value={form.partner_name}
                                            onChange={(e) => set("partner_name", e.target.value)}
                                        />
                                        {errors.partner_name && (
                                            <span className="cl-error-msg">{errors.partner_name}</span>
                                        )}
                                    </div>
                                    <div className="cl-col-2">
                                        <label className="cl-control-label">Partner ID</label>
                                        <input
                                            type="text"
                                            className="cl-form-control cl-readonly"
                                            placeholder="Auto"
                                            value={form.partner_id ?? ""}
                                            readOnly
                                        />
                                    </div>
                                </div>

                                {/* Row: Mobile Number | Password */}
                                <div className="cl-form-group cl-row">
                                    <div className="cl-col-6">
                                        <label className="cl-control-label">Mobile Number</label>
                                        <input
                                            type="text"
                                            className={`cl-form-control${errors.mobile ? " cl-input-error" : ""}`}
                                            placeholder="Mobile Number"
                                            maxLength={10}
                                            value={form.mobile}
                                            onChange={(e) =>
                                                set("mobile", e.target.value.replace(/\D/g, ""))
                                            }
                                        />
                                        {errors.mobile && (
                                            <span className="cl-error-msg">{errors.mobile}</span>
                                        )}
                                    </div>
                                </div>
                            </form>
                        </div>

                        {/* Modal Footer */}
                        <div className="cl-modal-footer">
                            <button className="cl-btn cl-btn-default" onClick={closeModal}>
                                Close
                            </button>
                            <button
                                className="cl-btn cl-btn-primary"
                                onClick={onSave}
                                disabled={saving}
                            >
                                {saving ? "Saving…" : form.partner_id ? "Update" : "Add"}
                            </button>
                        </div>

                    </div>
                </div>
            )}
        </div>
    );
};

export default Partners;